// Visitor names and room names.

/** Cursor names. Each visitor in a room gets a different one while there are enough to go around. */
export const ROLES = [
  "Director",
  "Editor",
  "Gaffer",
  "Colorist",
  "Writer",
  "Producer",
  "Composer",
  "Grip",
  "Scout",
  "Narrator",
  "Foley",
  "Archivist",
] as const;

export interface Peer {
  /** Connection id, unique per open socket. Cursors are keyed by it. */
  id: string;
  /** Stable per-tab client id. Strokes and undo are keyed by it. */
  owner: string;
  name: string;
}

/**
 * Picks a cursor name for a new socket. A returning visitor keeps their name when it
 * is still free. Otherwise the name comes from a stable hash of the client id,
 * skipping names in use, so two tabs never share a label.
 */
export function assignName(owner: string, others: readonly Pick<Peer, "name">[], preferred?: unknown): string {
  const taken = new Set(others.map((p) => p.name));
  const wanted = typeof preferred === "string" && (ROLES as readonly string[]).includes(preferred);
  let name = wanted && !taken.has(preferred) ? preferred : "";
  if (!name) {
    const start = hashString(owner) % ROLES.length;
    for (let i = 0; i < ROLES.length && !name; i++) {
      const role = ROLES[(start + i) % ROLES.length] ?? "Director";
      if (!taken.has(role)) name = role;
    }
    // More visitors than names: reuse one with a number.
    if (!name) name = `${ROLES[start] ?? "Director"} ${others.length + 1}`;
  }
  return name;
}

/** Small stable string hash (FNV-1a). */
export function hashString(value: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

const ROOM_ID = /^[a-z0-9][a-z0-9-]{2,47}$/;
const ROOM_ADJECTIVES = ["quiet", "late", "still", "slow", "soft", "night", "pale", "grey", "north", "first", "low", "long"];
const ROOM_NOUNS = ["sheet", "frame", "scene", "plan", "draft", "line", "grid", "study", "sketch", "plate"];

export function isValidRoomId(value: string): boolean {
  return ROOM_ID.test(value);
}

/** A readable, shareable room id such as "quiet-sheet-k3f9". */
export function generateRoomId(random: () => number = Math.random): string {
  const adjective = ROOM_ADJECTIVES[Math.floor(random() * ROOM_ADJECTIVES.length)] ?? "quiet";
  const noun = ROOM_NOUNS[Math.floor(random() * ROOM_NOUNS.length)] ?? "sheet";
  let suffix = "";
  for (let i = 0; i < 4; i++) suffix += "abcdefghjkmnpqrstuvwxyz23456789"[Math.floor(random() * 31)];
  return `${adjective}-${noun}-${suffix}`;
}
